import React, { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Box, Divider, Grid, Typography } from '@material-ui/core'
import { components } from 'cng-web-lib'
import { useParams } from 'react-router-dom'
import pathMap from 'src/paths/pathMap'
import MpciApiClient from '../Common/services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import FormProperties from './FormProperties'
import ParentCard from 'src/components/era-package/shared/ParentCard'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CngSection from 'src/components/cngcomponents/CngSection'
import { STATUS_CODES, SUB_STATUS_CODES, canUpdateFiling, canEditDraft } from '../Common/constants/billStatuses'
import AuditHistoryTable from '../Common/components/AuditHistoryTable'
import FilingUpdateInfoSection from '../Common/components/FilingUpdateInfoSection'
import SupportingDocumentsSection from '../Common/components/SupportingDocumentsSection'


const {
  button: { CngButton }
} = components

function ViewPage({ history, showNotification }) {
  const { id } = useParams()
  const [isFetching, setIsFetching] = useState(false)
  const [recordStatus, setRecordStatus] = useState('')
  const [recordSubStatus, setRecordSubStatus] = useState('')
  const [existingAttachments, setExistingAttachments] = useState([])

  const methods = useForm({
    defaultValues: { ...FormProperties.formikProps.initialValues }
  })

  useEffect(() => {
    MpciApiClient.getById(
      UaeMpciApiUrls.HOUSE_BILL_GET,
      id,
      (record) => {
        if (!record) {
          showNotification('error', 'Data not found.')
          history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)
        } else {
          methods.reset(FormProperties.toClientDataFormat(record))
          setRecordStatus(record.status || '')
          setRecordSubStatus(record.subStatus || '')
          setExistingAttachments(record.attachmentInfo || [])
        }
        setIsFetching(true)
      },
      () => {
        showNotification('error', 'Failed to load record.')
        history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)
        setIsFetching(true)
      }
    )
  }, [])

  function handleDownloadAttachment(attachment) {
    const url = `${UaeMpciApiUrls.ATTACHMENT_DOWNLOAD}/${attachment.id}`
    fetch(url, {
      method: 'GET',
      headers: { Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')) }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const disposition = res.headers.get('Content-Disposition') || ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const fileName = match ? match[1] : (attachment.fileName || `attachment_${attachment.id}`)
        return res.blob().then((blob) => ({ blob, fileName }))
      })
      .then(({ blob, fileName }) => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      })
      .catch((err) => {
        console.error('[HouseBill ViewPage] attachment download failed', err)
        showNotification('error', 'Download failed. Please try again.')
      })
  }

  if (!isFetching) return <p>Loading...</p>

  const showUpdateButton = canUpdateFiling(recordStatus, recordSubStatus)
  const showEditButton = canEditDraft(recordStatus, recordSubStatus)
  const isConnectivityIssue = recordSubStatus === SUB_STATUS_CODES.CI
  const hasSupportingDocs = existingAttachments.length > 0

  return (
    <ParentCard title='House Bill of Lading'>
      <FormProvider {...methods}>
        <form>
          {/* Only show when there's an update (handled internally) or actual supporting documents to view */}
          <FilingUpdateInfoSection forceShow={hasSupportingDocs}>
            {isConnectivityIssue && (
              <Typography variant='caption' color='textSecondary' style={{ display: 'block', margin: '12px 0' }}>
                This filing could not be submitted due to a connectivity issue. Supporting documents remain available for viewing below.
              </Typography>
            )}
            <Box mt={2}>
              <SupportingDocumentsSection
                attachments={existingAttachments}
                onDownload={handleDownloadAttachment}
              />
            </Box>
          </FilingUpdateInfoSection>

          <FormProperties.Fields
            disabled={true}
            showNotification={showNotification}
            shouldHideMap={{}}
            isCreateMode={false}
          />

          <Box mt={3}>
            <Divider />
          </Box>

          {recordStatus !== STATUS_CODES.DRAFT && (
            <Box mt={3}>
              <CngSection title='Audit History'>
                <AuditHistoryTable id={id} />
              </CngSection>
            </Box>
          )}

          <Box mt={2}>
            <Grid container justify='space-between'>
              <Grid item>
                <CngButton
                  color='secondary'
                  size='medium'
                  onClick={() => history.push(pathMap.UAE_MPCI_HOUSE_BILL_LIST_VIEW)}
                  startIcon={<FontAwesomeIcon icon={['fal', 'arrow-left']} />}
                >
                  Back to List
                </CngButton>
              </Grid>
              <Grid item>
                <Grid container spacing={1}>
                  {showEditButton && (
                    <Grid item>
                      <CngButton
                        color='primary'
                        size='medium'
                        onClick={() => history.push(`${pathMap.UAE_MPCI_HOUSE_BILL}/edit/${id}`)}
                        startIcon={<FontAwesomeIcon icon={['fal', 'pen']} />}
                      >
                        Edit
                      </CngButton>
                    </Grid>
                  )}
                  {showUpdateButton && (
                    <Grid item>
                      <CngButton
                        color='primary'
                        size='medium'
                        onClick={() => history.push(`${pathMap.UAE_MPCI_HOUSE_BILL}/edit/${id}`)}
                        startIcon={<FontAwesomeIcon icon={['fal', 'arrow-up-from-bracket']} />}
                      >
                        Update
                      </CngButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </ParentCard>
  )
}

export default ViewPage
